import {
	Conversation,
	InShort,
	RelatedPages,
	type Turn,
} from "@marketing/components/answer";
import { Article } from "@marketing/components/article";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { HERO_PRODUCTS } from "@marketing/site";
import { createFileRoute } from "@tanstack/react-router";
import { CheckIcon } from "lucide-react";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "What is Arabizi?",
		a: "Arabizi is Arabic written in Latin letters, with numbers for the sounds Latin has no letter for: 3 for ع, 7 for ح, 2 for ء. Many shoppers in Lebanon and the Gulf type it on their phones, so a shopping assistant has to read it as Arabic.",
	},
	{
		q: "Does it understand Arabizi?",
		a: "Yes. It reads Arabic typed in Latin letters and numbers, such as 3endkon or 7aleeb, finds the products it means and answers in Arabizi when that is how the shopper wrote.",
	},
	{
		q: "Can shoppers mix Lebanese, French and English?",
		a: "Yes. A message in Arabizi with English or French words in it is weighed as a whole, and the reply follows the language the shopper is using.",
	},
	{
		q: "Does it speak Lebanese on calls?",
		a: "Yes. On calls and voice notes it hears and speaks Lebanese Arabic through a Lebanese speech engine, with a voice chosen for your store.",
	},
	{
		q: "Can shoppers order on WhatsApp in Lebanon?",
		a: "Yes. Your WhatsApp Business number answers with product cards, builds the basket and sends a checkout the shopper confirms with a tap. Voice notes are answered too.",
	},
	{
		q: "Can I try it before talking to you?",
		a: "Yes. The public demo store, Hephon Market, runs on a real Lebanese quick-commerce catalog of about 15,000 products, priced in US dollars, at commerce.hephon.ai/c/demo.",
	},
];

const POINTS = [
	{
		title: "Arabizi, read properly",
		text: "3 for ع, 7 for ح, 2 for ء: it reads Arabic typed in Latin letters and finds what the shopper meant, so “7aleeb” finds milk.",
	},
	{
		title: "Mixed in one message",
		text: "Arabizi, English and French in the same sentence are weighed together, and the reply follows the shopper.",
	},
	{
		title: "Lebanese by voice",
		text: "On calls and voice notes it hears and speaks Lebanese Arabic as people say it.",
	},
	{
		title: "Replies your team approved",
		text: "A phrasebook of Lebanese replies, written or approved by your team for each situation, keeps the tone local.",
	},
];

const CHAT: Turn[] = [
	{
		from: "shopper",
		text: "Marhaba, 3endkon labneh w zeit zeitoun?",
		en: "Hi, do you have labneh and olive oil?",
	},
	{
		from: "assistant",
		text: "Ahla, eh 3enna, hayde l labneh w zeit l zeitoun yalli bi far3ak:",
		en: "Hi, yes we do, here is the labneh and olive oil at your branch:",
		note: "Read as Arabizi, two searches, one row each. Answered in Arabizi.",
	},
	{
		from: "shopper",
		text: "Fi labneh light?",
		en: "Is there a light labneh?",
	},
	{
		from: "assistant",
		text: "Eh, hayde l labneh l light:",
		en: "Yes, here is the light labneh:",
		note: "Stays in the same category.",
	},
	{
		from: "shopper",
		text: "Merci, w bade a3mol tabbouleh la 6",
		en: "Thanks, and I want to make tabbouleh for 6. (French and Arabizi)",
	},
	{
		from: "assistant",
		text: "Akid. Hayde l ghrad lal tabbouleh la 6 ashkhas, kel shi bil 7ajm l mazbout:",
		en: "Sure. Here is what you need for tabbouleh for 6, each in the right pack size:",
		note: "Recipe: ingredient lines with amounts, and for each the pack that covers it for the least money.",
	},
	{
		from: "shopper",
		text: "Parfait, zidon kellon",
		en: "Perfect, add them all.",
	},
	{
		from: "assistant",
		text: "Tamem, zedton. L basket jehze, bas ekbos confirm.",
		en: "Done, added. Your basket is ready, just tap confirm.",
		note: "Nothing is ordered until the shopper confirms.",
	},
];

const STEPS = [
	{
		title: "Connect your catalog",
		text: "From Shopify, WooCommerce, a product feed, our REST API or a custom connector, with each branch's prices and stock.",
	},
	{
		title: "Set it up and test it",
		text: "Its name, its Lebanese tone and phrasebook, and handover. Then tests built from your catalog run in Lebanese, Arabizi, English and French.",
	},
	{
		title: "Go live channel by channel",
		text: "In your app, on your WhatsApp number, then calls.",
	},
];

const RELATED = [
	{
		href: "/arabic-chatbot-for-ecommerce",
		label: "Arabic chatbot for ecommerce",
	},
	{ href: "/channels/whatsapp", label: "WhatsApp ordering" },
	{ href: "/channels/voice", label: "Voice ordering" },
	{ href: "/how-it-works", label: "How it works" },
	{ href: "/demo", label: "Book a demo" },
];

export const Route = createFileRoute("/_marketing/lebanon")({
	head: () =>
		marketingHead({
			title: "Lebanese & Arabizi AI Shopping Assistant | Hephon Commerce",
			description:
				"Lebanese AI shopping assistant that understands Lebanese Arabic, Arabizi, English and French, in your app, on WhatsApp and on calls with a Lebanese voice.",
			path: "/lebanon",
			breadcrumb: ["Lebanon"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: LebanonPage,
});

function LebanonPage() {
	return (
		<>
			<PageIntro
				kicker="Lebanon"
				title="It speaks Lebanese, the way people type it."
				lead="Lebanese Arabic, Arabizi, English and French, often in one message. The same assistant runs your app, your WhatsApp number and your calls."
			>
				<ArabiziVisual />
			</PageIntro>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce is an AI shopping assistant for Lebanese
					Arabic and Arabizi. It reads Lebanese as shoppers type it,
					in Arabic script or in Latin letters with numbers, mixed
					with English and French, and answers in the same language
					and script.
				</p>
				<p>
					It searches the retailer's live catalog with each branch's
					prices, fills the basket in the retailer's app, on its
					WhatsApp Business number and on calls, where a Lebanese
					speech engine hears and speaks the dialect. Nothing is
					ordered until the shopper confirms.
				</p>
			</InShort>

			<Section
				id="chat-title"
				tone="mist"
				title="A Lebanese chat, in three languages."
				lead="Arabizi, French and English in one conversation, the way people in Beirut text a shop."
				center
			>
				<Conversation
					turns={CHAT}
					caption="A sample conversation written for this page to show the behaviour. It is not a transcript of a real shopper."
				/>
			</Section>

			<Article>
				<h2 id="arabizi">What is Arabizi?</h2>
				<p>
					Arabizi is Arabic written in Latin letters, with numbers for
					the sounds Latin has no letter for: 3 for ع, 7 for ح, 2 for
					ء, 5 for خ. In Lebanon it is how many people text: "3endkon"
					is "do you have", "bade" is "I want", "7aleeb" is milk. The
					same word is spelled several ways, and no keyboard setting
					fixes that.
				</p>

				<h2 id="typed">
					Does it understand Lebanese Arabic as people type it?
				</h2>
				<p>
					Yes. Shoppers write شو في عندكن or "shu fi 3endkon", بدي or
					"badde", and mean the same thing. Hephon Commerce reads
					Arabic script and Arabizi as Arabic, and its product search
					combines keywords and meaning, so "7aleeb" finds milk and a
					brand typed in Latin letters finds the product spelled in
					Arabic. It replies in the script the shopper used.
				</p>

				<h2 id="mixing">Can shoppers mix French and English?</h2>
				<p>
					Yes, and in Lebanon they do: "merci", "parfait" and "please"
					land in the middle of Lebanese sentences. Each message is
					weighed as a whole, so a French word in an Arabizi sentence
					does not switch the reply to French. A shopper who writes
					fully in French or English gets a reply in French or
					English.
				</p>

				<h2 id="calls">Does it speak Lebanese on calls?</h2>
				<p>
					Yes. On calls and voice notes, Lebanese Arabic is heard and
					spoken through a Lebanese speech engine, so the assistant
					sounds like someone from Lebanon, not a Gulf or formal
					voice. It reads the basket back before anything is ordered,
					and the caller confirms on the keypad. A caller can always
					be handed to a person.
				</p>

				<h2 id="phrasebook">How does it keep the tone Lebanese?</h2>
				<p>
					Its fixed replies come from a phrasebook of approved
					Lebanese phrases for each situation, which your team can
					edit. That keeps "akid", "tamem" and "yalla" in the right
					places, rather than a translation from formal Arabic.
				</p>

				<h2 id="not-fit">When Hephon Commerce is not the right fit</h2>
				<ul>
					<li>
						You have no online catalog with prices and stock. The
						assistant needs them to work.
					</li>
					<li>
						You only need answers to support questions. A
						customer-service tool will cost less.
					</li>
					<li>
						You want self-serve signup and a public price. Access is
						by invitation and each retailer gets a proposal.
					</li>
				</ul>
			</Article>

			<Section
				id="lebanese-title"
				tone="mist"
				title="Built for how Lebanon shops."
			>
				<DetailGrid items={POINTS} columns={4} tone="mist" />
			</Section>

			<Section id="steps-title" title="Live in three steps." center>
				<Steps items={STEPS} />
			</Section>

			<Faq items={FAQ} title="Lebanese, answered." />
			<RelatedPages links={RELATED} />
			<Closing />
		</>
	);
}

const P = HERO_PRODUCTS;

/** A shopper writing in Arabizi and the assistant answering in kind, with products. */
function ArabiziVisual() {
	return (
		<div className="mx-auto max-w-md pb-10 text-start sm:pb-14">
			<p className="ms-auto w-fit max-w-[85%] rounded-[20px] rounded-br-md bg-(--mk-ink) px-4 py-2.5 text-(--mk-white) text-[15px]">
				3endkon chips w drinks lal match? We're 6
			</p>
			<p className="mt-3 flex items-center gap-2 text-(--mk-muted) text-[13px]">
				<span className="grid size-4 place-items-center rounded-full bg-(--mk-signal) text-(--mk-white)">
					<CheckIcon className="size-2.5" aria-hidden />
				</span>
				Read as Arabizi · Searched snacks and drinks
			</p>
			<p className="mt-3 w-fit max-w-[85%] rounded-[20px] rounded-bl-md bg-(--mk-white) px-4 py-2.5 text-[15px]">
				Akid, hayde l combo lal match, bi kafe 6:
			</p>
			<div className="mt-3 grid grid-cols-3 gap-2">
				{[P.doritos, P.sevenUp, P.cashews].map((p) => (
					<div key={p.id} className="rounded-2xl bg-(--mk-white) p-2">
						<img
							src={p.thumb}
							alt=""
							width={96}
							height={96}
							loading="lazy"
							className="aspect-square w-full object-contain"
						/>
						<p className="mt-1 truncate text-[12px]">{p.title}</p>
						<p className="font-semibold text-[13px]">${p.price}</p>
					</div>
				))}
			</div>
		</div>
	);
}
